import { useEffect, useState } from 'react'
import serverConfig from '@/config/serverConfig'
import { useLanguage } from '@/localization'

export interface CommunitySocial {
  label: string
  href: string
  icon: string
}

/** How prominently a team member is shown (controls card size). */
export type TeamTier = 'core' | 'admin' | 'moderator'

/** Which game sites a team member appears on. */
export type TeamScope = 'platform' | 'kf1' | 'minecraft'

export interface CommunityLeader {
  name: string
  role: string
  /** How prominently they're shown: core (big), admin (medium), moderator (compact). */
  tier: TeamTier
  /** "platform" for network-wide; a game id for game-specific staff. */
  scope: TeamScope
  /** Short role pill shown on the card (e.g. "Owner", "Core Team", "Admin", "Mod"). */
  badge?: string
  /** Avatar image URL; falls back to `initials` on error / when absent. */
  avatar?: string
  /** 2-letter fallback rendered when no avatar is available. */
  initials?: string
  /** Longer blurb shown on richer (core/admin) cards. */
  description?: string
  /** Skill chips shown on core cards. */
  specialties?: string[]
}

export interface CommunityConduct {
  title: string
  summary: string
  /** Detailed rules under this category (rendered in full on the hub /conduct page). */
  rules?: string[]
}

export interface Community {
  social: CommunitySocial[]
  discordInvite: string
  leadership: CommunityLeader[]
  conduct: CommunityConduct[]
}

/**
 * Inlined seed used as the fallback when /api/community is unreachable.
 * Discord/Steam hrefs default to the local serverConfig values so the
 * single-file config stays the ultimate source of truth for MC.
 */
const FALLBACK: Community = {
  social: [
    { label: 'Discord', href: serverConfig.community.discord, icon: 'discord' },
    { label: 'Steam', href: serverConfig.community.steam, icon: 'steam' },
  ],
  discordInvite: serverConfig.community.discord,
  leadership: [
    {
      name: 'Lytair',
      role: 'Owner & Community Lead',
      tier: 'core',
      scope: 'platform',
      badge: 'Owner',
      avatar: '/avatars/uliunai_avatar_lytair.png',
      initials: 'LY',
      description:
        'Leads the Uliunai.com community. Responsible for community moderation and ensuring a welcoming environment for all players.',
      specialties: ['Community Moderation', 'Player Support'],
    },
    {
      name: 'Greiton',
      role: 'Platform Engineer',
      tier: 'core',
      scope: 'platform',
      badge: 'Core Team',
      avatar: '/avatars/uliunai_avatar_greiton.png',
      initials: 'GR',
      description:
        'Built the Uliunai.com platform from the ground up. Handles the website, admin tools, server infrastructure, and deployment pipeline.',
      specialties: ['Web Development', 'Platform Design', 'Server Infrastructure'],
    },
    {
      name: 'HekuT',
      role: 'Game Developer',
      tier: 'core',
      scope: 'platform',
      badge: 'Core Team',
      avatar: '/avatars/uliunai_avatar_hekut.png',
      initials: 'HT',
      description:
        'The mind behind the gameplay. Develops custom mutators, perks, and weapons, and fine-tunes game balance to keep every match fresh and challenging.',
      specialties: ['Game Balancing', 'Mutator Development', 'Custom Content'],
    },
    {
      name: 'Jackie',
      role: 'Discord Administrator',
      tier: 'admin',
      scope: 'platform',
      badge: 'Admin',
      avatar: '/avatars/uliunai_avatar_jackie.png',
      initials: 'JA',
      description: 'Keeps the Discord running and supports the community day to day.',
    },
    {
      name: 'mez',
      role: 'Administrator',
      tier: 'admin',
      scope: 'platform',
      badge: 'Admin',
      avatar: '/avatars/uliunai_avatar_mez.png',
      initials: 'ME',
      description: 'Part of the admin team.',
    },
    { name: 'habib', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_habib.png', initials: 'HB' },
    { name: 'Hakk', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_hakk.png', initials: 'HK' },
    { name: 'Paisely', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_paisely.png', initials: 'PA' },
    { name: 'Spagh', role: 'Moderator', tier: 'moderator', scope: 'platform', badge: 'Mod', avatar: '/avatars/uliunai_avatar_spagh.png', initials: 'SP' },
  ],
  conduct: [
    { title: 'Be Respectful', summary: 'No insulting, provoking, harassment or discrimination.' },
    { title: 'Fair Play', summary: 'No cheating, exploits, or third-party software.' },
    { title: 'Keep Chat Clean', summary: 'No spam, flooding, impersonation, or advertising.' },
  ],
}

/**
 * Fetches the shared community data (`GET /api/community`, proxied to :8078)
 * once on mount. Returns the inlined seed immediately and swaps in the live
 * data when it arrives. Always resolves to a usable object — never throws.
 */
export default function useCommunity(): Community {
  const { apiPath } = useLanguage()
  const [community, setCommunity] = useState<Community>(FALLBACK)

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()

    async function load() {
      try {
        const res = await fetch(apiPath('/api/community'), { signal: controller.signal })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = (await res.json()) as Partial<Community>
        if (cancelled) return
        setCommunity({
          social: Array.isArray(data.social) && data.social.length ? data.social : FALLBACK.social,
          discordInvite: data.discordInvite || FALLBACK.discordInvite,
          leadership:
            Array.isArray(data.leadership) && data.leadership.length
              ? data.leadership
              : FALLBACK.leadership,
          conduct:
            Array.isArray(data.conduct) && data.conduct.length ? data.conduct : FALLBACK.conduct,
        })
      } catch (err) {
        if (cancelled || (err as Error).name === 'AbortError') return
        // Keep the fallback already in state.
      }
    }

    load()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [apiPath])

  return community
}
